import { useLocation } from "react-router-dom";

interface BreadcrumbItem {
	name: string;
	path: string;
}

interface SchemaBreadcrumbProps {
	/**
	 * Custom breadcrumb items. If not provided, will generate from current URL.
	 */
	items?: BreadcrumbItem[];
	/**
	 * Base URL for the site (default: https://portalcib.online)
	 */
	baseUrl?: string;
}

/**
 * SchemaBreadcrumb Component
 *
 * Generates BreadcrumbList structured data (JSON-LD) for search engines.
 * Helps display breadcrumb navigation in search results.
 *
 * @example
 * // Auto-generate from current URL
 * <SchemaBreadcrumb />
 *
 * @example
 * // Custom breadcrumbs
 * <SchemaBreadcrumb items={[
 *   { name: "Home", path: "/" },
 *   { name: "Notícias", path: "/noticias" },
 *   { name: "CIB é implementado", path: "/noticias/cib-implementado" }
 * ]} />
 */
const SchemaBreadcrumb = ({
	items,
	baseUrl = "https://portalcib.online",
}: SchemaBreadcrumbProps) => {
	const location = useLocation();

	// Generate breadcrumb items from URL if not provided
	const breadcrumbItems: BreadcrumbItem[] =
		items ?? generateBreadcrumbsFromPath(location.pathname, location.search);

	if (breadcrumbItems.length < 2) return null;

	const schemaData = {
		"@context": "https://schema.org",
		"@type": "BreadcrumbList",
		itemListElement: breadcrumbItems.map((item, index) => ({
			"@type": "ListItem",
			position: index + 1,
			name: item.name,
			item: `${baseUrl}${item.path}`,
		})),
	};

	return (
		<script
			type="application/ld+json"
			// biome-ignore lint/security/noDangerouslySetInnerHtml: <>
			dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
		/>
	);
};

/**
 * Generate breadcrumb items from a URL path
 */
function generateBreadcrumbsFromPath(
	pathname: string,
	search: string,
): BreadcrumbItem[] {
	const items: BreadcrumbItem[] = [{ name: "Início", path: "/" }];

	// Remove leading/trailing slashes and split
	const segments = pathname.replace(/^\/|\/$/g, "").split("/");

	// Build breadcrumb path incrementally
	let currentPath = "";

	for (const segment of segments) {
		if (!segment) continue;

		currentPath += `/${segment}`;

		// Skip article/news detail IDs for cleaner breadcrumbs
		if (
			segments[0] === "noticias" &&
			segments.length === 2 &&
			segment === segments[1]
		) {
			items.push({ name: "Notícia", path: currentPath + search });
			continue;
		}

		if (
			segments[0] === "article" &&
			segments.length === 2 &&
			segment === segments[1]
		) {
			items.push({ name: "Artigo", path: currentPath + search });
			continue;
		}

		// Convert slug to readable name
		const name = slugToName(segment);
		items.push({ name, path: currentPath });
	}

	return items;
}

/**
 * Convert URL slug to readable name
 */
function slugToName(slug: string): string {
	// Route name mappings
	const routeNames: Record<string, string> = {
		noticias: "Notícias",
		cursos: "Cursos",
		ferramentas: "Ferramentas",
		dicionario: "Dicionário",
		sobre: "Sobre",
		privacy: "Privacidade",
		terms: "Termos de Uso",
		contact: "Contato",
		article: "Artigos",
	};

	// Return mapped name or title-case the slug
	return (
		routeNames[slug] ??
		slug
			.split(/[-_]/)
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
			.join(" ")
	);
}

export default SchemaBreadcrumb;
